import { useEffect, useState, useMemo } from "react";
import { Container, Row, Col, Form, Button, InputGroup, Badge, ListGroup, Spinner, Alert, Modal, Collapse } from "react-bootstrap";
import { Filter, Search } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";

import { useTitle } from "./context";
import { SpellBookBackend } from "./backend";
import { fetchSchools, fetchClasses, fetchDescriptors, storeState, loadState, fetchSubSchools, resetScroll, useElementHeight } from "./utilities";
import { AlphabeticalSpellsList } from "./spellsList";

import "bootstrap/dist/css/bootstrap.min.css";


const SEARCH_PAGE_KEY = "search-page";

const SEARCH_PAGE_SHOW_FILTERS_KEY = `${SEARCH_PAGE_KEY}.show-filters`;
const SEARCH_PAGE_SEARCH_VALID_KEY = `${SEARCH_PAGE_KEY}.search-valid`;
const SEARCH_PAGE_SCHOOLS_KEY = `${SEARCH_PAGE_KEY}.schools`;
const SEARCH_PAGE_SUB_SCHOOLS_KEY = `${SEARCH_PAGE_KEY}.sub-schools`;
const SEARCH_PAGE_DESCRIPTORS_KEY = `${SEARCH_PAGE_KEY}.descriptors`;
const SEARCH_PAGE_CLASSES_KEY = `${SEARCH_PAGE_KEY}.classes`;

const SEARCH_PAGE_QUERY_KEY = `${SEARCH_PAGE_KEY}.query`;
const SEARCH_PAGE_SELECTED_SCHOOLS_KEY = `${SEARCH_PAGE_KEY}.selected.schools`;
const SEARCH_PAGE_SELECTED_SUB_SCHOOLS_KEY = `${SEARCH_PAGE_KEY}.selected.sub-schools`;
const SEARCH_PAGE_SELECTED_DESCRIPTORS_KEY = `${SEARCH_PAGE_KEY}.selected.descriptors`;
const SEARCH_PAGE_SELECTED_CLASSES_KEY = `${SEARCH_PAGE_KEY}.selected.classes`;
const SEARCH_PAGE_SELECTED_SPELL_RESISTANCE_KEY = `${SEARCH_PAGE_KEY}.selected.spell-resistance`;
const SEARCH_PAGE_SELECTED_SEARCH_IN_DESCRIPTION_KEY = `${SEARCH_PAGE_KEY}.selected.search-in-description`;

const SEARCH_PAGE_RESULT_SPELLS_KEY = `${SEARCH_PAGE_KEY}.results.spells`;


export function SearchPage({navbarHeight}) {
    const navigate = useNavigate();
    const { setTitle } = useTitle();

    const [searchBarRef, searchBarHeight] = useElementHeight();

    const [showFilters, setShowFilters] = useState(null);
    const [openSelection, setOpenSelection] = useState(null);
    const [areSearchResultsValid, setAreSearchResultsValid] = useState(null);

    const [schools, setSchools] = useState(null);
    const [subSchools, setSubSchools] = useState(null);
    const [descriptors, setDescriptors] = useState(null);
    const [classes, setClasses] = useState(null);
    
    const [query, setQuery] = useState(null);
    const [selectedSchools, setSelectedSchools] = useState(null);
    const [selectedSubSchools, setSelectedSubSchools] = useState(null);
    const [selectedDescriptors, setSelectedDescriptors] = useState(null);
    const [selectedClasses, setSelectedClasses] = useState(null);
    const [selectedSpellResistance, setSelectedSpellResistance] = useState(null); // 0, 1, -1
    const [searchInDescription, setSearchInDescription] = useState(null);

    const [resultSpells, setResultSpells] = useState(null);
    
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [error, setError] = useState("");
    const [searchStatus, setSearchStatus] = useState("idle") // idle | loading | success | error
    const [searchError, setSearchError] = useState("");
    
    useEffect(() => {
        setTitle("Ricerca");
    }, [setTitle]);
    
    const selectedItemsChanged = (before, after) => {
        if ((before === null) && (after === null)) return false;
        if ((before === null) || (after === null)) return true;
        if (before.length != after.length) return true;
        for (const a in after) {
            const hadBefore = before.some((i) => i.id == a.id);
            if (!hadBefore) return true;
        }
        return false;
    };

    const selectedBooleanChanged = (before, after) => {
        if ((before === null) != (after === null)) return true;
        return before !== after;
    };
  
    const resetFilters = () => {
        doSetSelectedSchools([]);
        doSetSelectedSubSchools([]);
        doSetSelectedDescriptors([]);
        doSetSelectedClasses([]);
        doSetSelectedSpellResistance(-1);
        doSetSearchInDescription(false);
    };

    const doSetQuery = (v) => {
        if (v != query) {
            setQuery(v);
            setAreSearchResultsValid(false);
        }
    }

    const doSetSelectedSchools = (v) => {
        if (selectedItemsChanged(selectedSchools, v)) {
            setSelectedSchools(v);
            setAreSearchResultsValid(false);
        }
    };

    const doSetSelectedSubSchools = (v) => {
        if (selectedItemsChanged(selectedSubSchools, v)) {
            setSelectedSubSchools(v);
            setAreSearchResultsValid(false);
        }
    };

    const doSetSelectedDescriptors = (v) => {
        if (selectedItemsChanged(selectedDescriptors, v)) {
            setSelectedDescriptors(v);
            setAreSearchResultsValid(false);
        }
    };

    const doSetSelectedClasses = (v) => {
        if (selectedItemsChanged(selectedClasses, v)) {
            setSelectedClasses(v);
            setAreSearchResultsValid(false);
        }
    };

    const doSetSelectedSpellResistance = (v) => {
        if (selectedBooleanChanged(selectedSpellResistance, v)) {
            setSelectedSpellResistance(v);
            setAreSearchResultsValid(false);
        }
    };

    const doSetSearchInDescription = (v) => {
        if (selectedBooleanChanged(searchInDescription, v)) {
            setSearchInDescription(v);
            setAreSearchResultsValid(false);
        }
    };

    const loadStateIfDefined = (key, loadFunc) => {
        const s = loadState(key);
        if (s != null) {
            loadFunc(s);
            return true;
        }
        return false;
    };

    useEffect(() => {
        if (showFilters != null) {
            storeState(SEARCH_PAGE_SHOW_FILTERS_KEY, showFilters);
        }
    }, [showFilters]);

    useEffect(() => {
        if (areSearchResultsValid != null) {
            storeState(SEARCH_PAGE_SEARCH_VALID_KEY, areSearchResultsValid);
        }
    }, [areSearchResultsValid]);

    useEffect(() => {
        if (schools && schools.length > 0) {
            storeState(SEARCH_PAGE_SCHOOLS_KEY, schools);
        }
    }, [schools]);

    useEffect(() => {
        if (subSchools && subSchools.length > 0) {
            storeState(SEARCH_PAGE_SUB_SCHOOLS_KEY, subSchools);
        }
    }, [subSchools]);

    useEffect(() => {
        if (descriptors && descriptors.length > 0) {
            storeState(SEARCH_PAGE_DESCRIPTORS_KEY, descriptors);
        }
    }, [descriptors]);

    useEffect(() => {
        if (classes && classes.length > 0) {
            storeState(SEARCH_PAGE_CLASSES_KEY, classes);
        }
    }, [classes]);

    const loadBaseState = () => {
        let success = true;
        if (!loadStateIfDefined(SEARCH_PAGE_SHOW_FILTERS_KEY, setShowFilters)) success = false;
        if (!loadStateIfDefined(SEARCH_PAGE_SEARCH_VALID_KEY, setAreSearchResultsValid)) success = false;
        if (!loadStateIfDefined(SEARCH_PAGE_SCHOOLS_KEY, setSchools)) success = false;
        if (!loadStateIfDefined(SEARCH_PAGE_SUB_SCHOOLS_KEY, setSubSchools)) success = false;
        if (!loadStateIfDefined(SEARCH_PAGE_DESCRIPTORS_KEY, setDescriptors)) success = false;
        if (!loadStateIfDefined(SEARCH_PAGE_CLASSES_KEY, setClasses)) success = false;
        return success;
    };

    useEffect(() => {
        if (query != null) {
            storeState(SEARCH_PAGE_QUERY_KEY, query);
        }
    }, [query]);

    useEffect(() => {
        if (selectedSchools != null) {
            storeState(SEARCH_PAGE_SELECTED_SCHOOLS_KEY, selectedSchools);
        }
    }, [selectedSchools]);

    useEffect(() => {
        if (selectedSubSchools != null) {
            storeState(SEARCH_PAGE_SELECTED_SUB_SCHOOLS_KEY, selectedSubSchools);
        }
    }, [selectedSubSchools]);

    useEffect(() => {
        if (selectedDescriptors != null) {
            storeState(SEARCH_PAGE_SELECTED_DESCRIPTORS_KEY, selectedDescriptors);
        }
    }, [selectedDescriptors]);

    useEffect(() => {
        if (selectedClasses != null) {
            storeState(SEARCH_PAGE_SELECTED_CLASSES_KEY, selectedClasses);
        }
    }, [selectedClasses]);

    useEffect(() => {
        if (selectedSpellResistance != null) {
            storeState(SEARCH_PAGE_SELECTED_SPELL_RESISTANCE_KEY, selectedSpellResistance);
        }
    }, [selectedSpellResistance]);

    useEffect(() => {
        if (searchInDescription != null) {
            storeState(SEARCH_PAGE_SELECTED_SEARCH_IN_DESCRIPTION_KEY, searchInDescription);
        }
    }, [searchInDescription]);

    const loadSelectionState = () => {
        let success = true;
        if (!loadStateIfDefined(SEARCH_PAGE_QUERY_KEY, setQuery)) success = false;
        if (!loadStateIfDefined(SEARCH_PAGE_SELECTED_SCHOOLS_KEY, setSelectedSchools)) success = false;
        if (!loadStateIfDefined(SEARCH_PAGE_SELECTED_SUB_SCHOOLS_KEY, setSelectedSubSchools)) success = false;
        if (!loadStateIfDefined(SEARCH_PAGE_SELECTED_DESCRIPTORS_KEY, setSelectedDescriptors)) success = false;
        if (!loadStateIfDefined(SEARCH_PAGE_SELECTED_CLASSES_KEY, setSelectedClasses)) success = false;
        if (!loadStateIfDefined(SEARCH_PAGE_SELECTED_SPELL_RESISTANCE_KEY, setSelectedSpellResistance)) success = false;
        if (!loadStateIfDefined(SEARCH_PAGE_SELECTED_SEARCH_IN_DESCRIPTION_KEY, setSearchInDescription)) success = false;
        return success;
    };

    useEffect(() => {
        if (resultSpells != null) {
            storeState(SEARCH_PAGE_RESULT_SPELLS_KEY, resultSpells);
        }
    }, [resultSpells]);

    const loadResultsState = () => {
        return loadStateIfDefined(SEARCH_PAGE_RESULT_SPELLS_KEY, setResultSpells);
    };
    
    const fetchData = async () => {
        setSchools(await fetchSchools());
        setSubSchools(await fetchSubSchools());
        setDescriptors(await fetchDescriptors());
        setClasses(await fetchClasses());
    };

    useEffect(() => {
        let mounted = true;
        (
            async () => {
                try {
                    setStatus("loading");

                    // Load the base state
                    if (!loadBaseState()) {
                        await fetchData();
                        setShowFilters(false);
                        setAreSearchResultsValid(false);
                    }

                    // Load the selection state (if any)
                    if (!loadSelectionState()) {
                        resetFilters();
                        setQuery("");
                    }

                    // Load the results (if any)
                    if (!loadResultsState()) {
                        setResultSpells([]);
                    }

                    if (mounted) {
                        setStatus("success");
                    }
                }
                catch {
                    if (mounted) {
                        setError("Failed to load data.");
                        setStatus("error");
                    }
                }
            }
        )();
        return () => {
            mounted = false;
        };
    }, []);

    const toggleShowFilters = () => {
        setShowFilters(showFilters == null ? true : !showFilters);
    };

    const toggleSpellResistance = () => {
        if (selectedSpellResistance === 0 || selectedSpellResistance === false) doSetSelectedSpellResistance(1);
        else if (selectedSpellResistance === 1 || selectedSpellResistance === true) doSetSelectedSpellResistance(-1);
        else doSetSelectedSpellResistance(0);
    };

    const toggleSearchInDescription = () => {
        if (searchInDescription === true) doSetSearchInDescription(false);
        else doSetSearchInDescription(true);
    };
    
    const multiSelectionDisplayValue = (arr) => {
        if (arr && arr.length > 0) {
            return arr.map((a) => a.name).join(", ");
        }
        return "Qualsiasi";
    };
    
    const spellResistanceDisplayValue = (sr) => {
        if (sr === 1 || sr === true) return "Sì";
        if (sr === 0 || sr === false) return "No";
        return "Qualsiasi";
    };

    const spellResistanceFilterCount = (sr) => {
        if (sr === 1 || sr === true || sr === 0 || sr === false) return 1;
        return 0;
    };

    const spellResistanceSearchParameter = (sr) => {
        if (sr === 0 || sr === false) return false;
        if (sr === 1 || sr === true) return true;
        return null;
    }

    // Prepare a compact summary (useful for UI)
    const activeFiltersCount = useMemo(() => {
        let c = 0;
        if (selectedSchools?.length) c += 1;
        if (selectedSubSchools?.length) c += 1;
        if (selectedDescriptors?.length) c += 1;
        if (selectedClasses?.length) c += 1;
        c += spellResistanceFilterCount(selectedSpellResistance);
        if (searchInDescription === true) c += 1;
        return c;
    }, [
        selectedSchools,
        selectedSubSchools,
        selectedDescriptors,
        selectedClasses,
        selectedSpellResistance,
        searchInDescription
    ]);

    // Search action: normalize and call your backend
    const runSearch = async (e) => {
        if (e && e.preventDefault) e.preventDefault();

        setSearchStatus("loading");

        try {
            const spells = await SpellBookBackend.searchSpells(
                query.trim(),
                searchInDescription,
                searchInDescription,
                selectedSchools,
                selectedSubSchools,
                selectedDescriptors,
                selectedClasses,
                spellResistanceSearchParameter(selectedSpellResistance)
            );
            
            setResultSpells(spells);
            setAreSearchResultsValid(true);

            resetScroll(SEARCH_PAGE_KEY);
            
            setSearchStatus("success");
            setSearchError("");
        }
        catch (err) {
            setSearchError(err ?? "Error during search");
            setSearchStatus("error");
        }
    };

    const searchBar = useMemo(() => {
        if (status === "loading") {
            return (
                <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" role="status" aria-label="Loading items" />
                </div>
            );
        }
        if (status === "error") {
            return <Alert variant="danger" className="my-3">{error}</Alert>;
        }

        //else

        return (
            <Container ref={searchBarRef} fluid className="py-0">
                <Form onSubmit={runSearch}  className="pt-3 pb-1">
                    <Row className="mb-2 align-items-center">
                        <Col className="d-flex">
                            <InputGroup>
                                <Form.Control
                                    placeholder="Cerca incantesimi…"
                                    value={query ?? ""}
                                    onChange={(e) => doSetQuery(e.target.value)}
                                    aria-label="Ricerca principale"
                                />
                                <Button type="submit" variant={areSearchResultsValid ? "secondary" : "primary"}>
                                    <Search className="me-2"/>
                                    Cerca
                                </Button>
                                <Button variant="outline-secondary" onClick={() => toggleShowFilters()}>
                                    <Filter className="me-2"/>
                                    Filtri
                                    <Badge className="ms-2" bg={activeFiltersCount ? "primary" : "secondary"}>{activeFiltersCount}</Badge>
                                </Button>
                            </InputGroup>
                        </Col>
                    </Row>

                    <Collapse in={showFilters ?? false}>
                        <div id="filters-collapse">
                            <ListGroup className="pb-2">
                                <FilterRow
                                    label="Scuole"
                                    valueText={multiSelectionDisplayValue(selectedSchools)}
                                    count={selectedSchools?.length || 0}
                                    onClick={() => setOpenSelection("schools")}
                                    disabled={status === "loading"}
                                />
                                <FilterRow
                                    label="Sottoscuole"
                                    valueText={multiSelectionDisplayValue(selectedSubSchools)}
                                    count={selectedSubSchools?.length || 0}
                                    onClick={() => setOpenSelection("sub-schools")}
                                    disabled={status === "loading"}
                                />
                                <FilterRow
                                    label="Descrittori"
                                    valueText={multiSelectionDisplayValue(selectedDescriptors)}
                                    count={selectedDescriptors?.length || 0}
                                    onClick={() => setOpenSelection("descriptors")}
                                    disabled={status === "loading"}
                                />
                                <FilterRow
                                    label="Classi"
                                    valueText={multiSelectionDisplayValue(selectedClasses)}
                                    count={selectedClasses?.length || 0}
                                    onClick={() => setOpenSelection("classes")}
                                    disabled={status === "loading"}
                                />
                                <FilterRow
                                    label="Resistenza agli incantesimi"
                                    valueText={spellResistanceDisplayValue(selectedSpellResistance)}
                                    count={spellResistanceFilterCount(selectedSpellResistance)}
                                    onClick={toggleSpellResistance}
                                    disabled={status === "loading"}
                                />
                                <FilterRow
                                    label="Cerca nella descrizione"
                                    valueText={searchInDescription === true ? "Sì" : "No"}
                                    count={searchInDescription === true ? 1 : 0}
                                    onClick={toggleSearchInDescription}
                                    disabled={status === "loading"}
                                />
                                <Button className="mt-2" variant="outline-danger" onClick={resetFilters}>
                                    Reimposta filtri
                                </Button>
                            </ListGroup>
                        </div>
                    </Collapse>
                </Form>

                <MultiFilterModal
                    title="Scuole"
                    show={openSelection === "schools"}
                    options={schools}
                    value={selectedSchools}
                    onClose={() => setOpenSelection(null)}
                    onApply={(vals) => {
                        doSetSelectedSchools(vals?.length ? vals : null);
                        setOpenSelection(null);
                    }}
                />
                <MultiFilterModal
                    title="Sottoscuole"
                    show={openSelection === "sub-schools"}
                    options={subSchools}
                    value={selectedSubSchools}
                    onClose={() => setOpenSelection(null)}
                    onApply={(vals) => {
                        doSetSelectedSubSchools(vals?.length ? vals : null);
                        setOpenSelection(null);
                    }}
                />
                <MultiFilterModal
                    title="Descrittori"
                    show={openSelection === "descriptors"}
                    options={descriptors}
                    value={selectedDescriptors}
                    onClose={() => setOpenSelection(null)}
                    onApply={(vals) => {
                        doSetSelectedDescriptors(vals?.length ? vals : null);
                        setOpenSelection(null);
                    }}
                />
                <MultiFilterModal
                    title="Classi"
                    show={openSelection === "classes"}
                    options={classes}
                    value={selectedClasses}
                    onClose={() => setOpenSelection(null)}
                    onApply={(vals) => {
                        doSetSelectedClasses(vals?.length ? vals : null);
                        setOpenSelection(null);
                    }}
                />
            </Container>
        );
    }, [
        error,
        showFilters,
        openSelection,
        areSearchResultsValid,
        query,
        schools,
        subSchools,
        descriptors,
        classes,
        selectedSchools,
        selectedSubSchools,
        selectedDescriptors,
        selectedClasses,
        selectedSpellResistance,
        searchInDescription,
        navigate
    ]);

    const searchResults = useMemo(() => {
        const spellsListHeight = `calc(100vh - ${navbarHeight}px - ${searchBarHeight}px)`;

        if (searchStatus === "loading") {
            return (
                <div className="d-flex justify-content-center py-5" style={{ height: spellsListHeight }}>
                    <Spinner animation="border" role="status" aria-label="Loading items" />
                </div>
            );
        }
        if (searchStatus === "error") {
            return <Alert variant="danger" className="my-3">{error}</Alert>;
        }

        //else
            
        return (
            <AlphabeticalSpellsList
            stateKey={SEARCH_PAGE_KEY}
            spells={resultSpells ?? []}
            onSpellClick={(spellId) => navigate(`/spells/${spellId}`)}
            grayedOut={areSearchResultsValid === false}
            height={spellsListHeight}/>
        );
    }, [
        searchStatus,
        searchError,
        resultSpells,
        areSearchResultsValid,
        searchBarHeight,
        navbarHeight,
        navigate
    ]);

    const containerMargin = `${navbarHeight}px`;

    return (
        <Container
        fluid
        className="bg-light p-0"
        style={{ marginTop: containerMargin }}>
            {searchBar}
            {searchResults}
        </Container>
    );
}


function FilterRow({ label, valueText, count, onClick, disabled, badgeVariant }) {
    return (
        <ListGroup.Item
        action
        onClick={disabled ? undefined : onClick}
        className="d-flex justify-content-between align-items-center py-1"
        disabled={disabled}
        type="button">
            <div>
                <small className="fw-semibold">{`${label}:`}</small>
                <span><small className="text-muted px-1">{valueText}</small></span>
            </div>
            <div className="d-flex align-items-center">
                {typeof count === "number" ? (<Badge bg={count ? (badgeVariant || "primary") : "secondary"}>{count}</Badge>) : null}
                {disabled && <Spinner animation="border" size="sm" className="ms-2" />}
            </div>
        </ListGroup.Item>
    );
}

function MultiFilterModal({ title, show, onClose, onApply, options, value }) {
    const [tempSelection, setTempSelection] = useState(value ?? []);

    useEffect(() => {
        setTempSelection(value ?? []);
    }, [show, value]);

    const tempSelectionContains = (id) => {
        return tempSelection.some((t) => t.id == id);
    }
    
    const toggleTempSelection = (opt) => {
        if (tempSelectionContains(opt.id)) {
            setTempSelection(tempSelection.filter((x) => x.id !== opt.id));
        }
        else {
            setTempSelection([...tempSelection, opt]);
        }
    };

    const clear = () => {
        setTempSelection([]);
    };

    return (
        <Modal show={show} onHide={onClose} centered scrollable restoreFocus={false}>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <ListGroup>
                    {options?.map((opt) => (
                        <ListGroup.Item
                        key={opt.id}
                        action
                        onClick={() => toggleTempSelection(opt)}
                        className="d-flex justify-content-between align-items-center">
                            <span>{opt.name}</span>
                            <Form.Check type="checkbox" readOnly checked={tempSelectionContains(opt.id)}/>
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                <Button variant="outline-secondary" onClick={clear}>Pulisci (Qualsiasi)</Button>
                <div className="d-flex gap-2">
                    <Button variant="secondary" onClick={onClose}>Annulla</Button>
                    <Button variant="primary" onClick={() => onApply(tempSelection)}>Applica</Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}
